import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { FC } from 'react'
import { tokens } from '@/theme'
import {
  CANAL_RECORTE_COR,
  CANAL_RECORTE_LABEL,
  CANAL_RECORTE_LABEL_CURTO,
  formatBRL,
  formatBRLCurto,
  formatPct,
} from './canais'
import type { CanalDeRecorte } from './canais'

export interface FatiaCanal {
  canal: CanalDeRecorte
  /** Volume em carteira do canal — soma dos status não terminais do recorte. */
  volume: number
}

const ALTURA_GRAFICO = 150

/**
 * Distribuição por canal da Visão de Carteira: colunas verticais com a
 * participação (%) de cada canal no volume em carteira e, abaixo, a lista com
 * o R$ exato. Os volumes vêm do `/v1/dashboard/resumo?canal=` de cada canal; o
 * % é apresentação (participação no total exibido). MUI/Box puro, sem lib de gráfico.
 */
export const DistribuicaoPorCanal: FC<{ fatias: FatiaCanal[] }> = ({ fatias }) => {
  const total = fatias.reduce((acc, f) => acc + f.volume, 0)
  const maior = Math.max(1, ...fatias.map((f) => f.volume))

  return (
    <Box>
      {/* Total no bloco laranja suave da marca (mesmo tint dos hovers do tema). */}
      <Box
        sx={{
          mb: 2,
          px: 1.5,
          py: 1.25,
          borderRadius: 2,
          backgroundColor: tokens.brandPrimarySoft,
          borderLeft: `4px solid ${tokens.brandPrimary}`,
        }}
      >
        <Typography
          variant="caption"
          component="div"
          sx={{
            textTransform: 'uppercase',
            fontWeight: 700,
            letterSpacing: '0.06em',
            color: tokens.brandPrimaryDark,
          }}
        >
          Total em carteira
        </Typography>
        <Typography
          component="div"
          sx={{
            fontSize: '1.25rem',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: tokens.brandInk,
          }}
        >
          {formatBRL(total)}
        </Typography>
      </Box>

      {/* Colunas verticais: altura relativa ao maior canal; rótulo com o %. */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-end"
        sx={{
          height: ALTURA_GRAFICO + 24,
          borderBottom: `1px solid ${tokens.divider}`,
          mb: 1,
        }}
      >
        {fatias.map((f) => {
          const altura = f.volume > 0 ? Math.max((f.volume / maior) * ALTURA_GRAFICO, 6) : 3
          return (
            <Box
              key={f.canal}
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1,
                  color: f.volume > 0 ? tokens.brandInk : tokens.textSecondary,
                }}
              >
                {formatPct(f.volume, total)}
              </Typography>
              <Tooltip title={`${CANAL_RECORTE_LABEL[f.canal]}: ${formatBRL(f.volume)}`} arrow>
                <Box
                  role="img"
                  aria-label={`${CANAL_RECORTE_LABEL[f.canal]}: ${formatBRL(f.volume)} (${formatPct(f.volume, total)})`}
                  sx={{
                    width: '100%',
                    maxWidth: 44,
                    height: altura,
                    borderRadius: '4px 4px 0 0',
                    backgroundColor: CANAL_RECORTE_COR[f.canal],
                    opacity: f.volume > 0 ? 1 : 0.3,
                    transition: 'height 200ms',
                  }}
                />
              </Tooltip>
            </Box>
          )
        })}
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        {fatias.map((f) => (
          <Typography
            key={f.canal}
            variant="caption"
            sx={{
              flex: 1,
              minWidth: 0,
              textAlign: 'center',
              color: tokens.textSecondary,
              fontSize: '0.6875rem',
              lineHeight: 1.2,
            }}
          >
            {CANAL_RECORTE_LABEL_CURTO[f.canal]}
          </Typography>
        ))}
      </Stack>

      {/* Lista com o valor exato de cada canal. */}
      <Stack spacing={0.75}>
        {fatias.map((f) => (
          <Box key={f.canal} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '3px',
                flexShrink: 0,
                backgroundColor: CANAL_RECORTE_COR[f.canal],
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: tokens.brandInk,
                flex: 1,
                minWidth: 0,
              }}
              noWrap
            >
              {CANAL_RECORTE_LABEL[f.canal]}
            </Typography>
            <Tooltip title={formatBRL(f.volume)} arrow>
              <Typography
                variant="caption"
                sx={{ color: tokens.textSecondary, whiteSpace: 'nowrap' }}
              >
                {formatBRLCurto(f.volume)} · {formatPct(f.volume, total)}
              </Typography>
            </Tooltip>
          </Box>
        ))}
      </Stack>
    </Box>
  )
}

export default DistribuicaoPorCanal
